import React, { useEffect, useState } from "react";
import { useParams } from "react-router-dom";
import axios from "axios";
import "./Receipt.css";
import { FaSearch, FaWifi, FaWifiSlash, FaPrint, FaUndo } from "react-icons/fa";

const Receipt = () => {
  const { sale_id } = useParams();
  const [state, setState] = useState({
    sale: null,
    customer: null,
    businessInfo: {
      heading1: "Gateway POS",
      heading2: "The Real Save",
      name: "Gateway POS",
      contact: "",
      ntn: "",
      strn: "",
      cashier: "Demo"
    },
    isLoading: true,
    isPrinted: false,
    isOffline: false
  });

  const baseURL = process.env.REACT_APP_BASE_URL;
  const token = localStorage.getItem("token");

  // Function to load offline sale data
  const loadOfflineSale = (saleId) => {
    try {
      const offlineSales = JSON.parse(localStorage.getItem('offline_sales')) || [];
      const offlineSale = offlineSales.find(sale => sale.id === saleId);
      
      if (!offlineSale) return null;

      // Transform offline sale to match API structure
      return {
        ...offlineSale,
        reference: `OFFLINE-${saleId.replace('offline_', '')}`,
        date: new Date(offlineSale.timestamp).toLocaleString(),
        items: offlineSale.items.map(item => ({
          ...item,
          product_name: item.name || "Product",
          grand_total: (item.price * item.quantity).toFixed(2)
        })),
        total_amount: offlineSale.total_amount,
        change_amount: offlineSale.change_amount || 0
      };
    } catch (error) {
      console.error("Error loading offline sale:", error);
      return null;
    }
  };

  useEffect(() => {
    let isMounted = true;
    const controller = new AbortController();

    const loadData = async () => {
      try {
        // First try to load from server
        const saleRes = await axios.get(`${baseURL}/sales/${sale_id}`, {
          headers: { Authorization: `Bearer ${token}` },
          signal: controller.signal,
          timeout: 5000 // Timeout after 5 seconds
        });

        if (!isMounted) return;

        const sale = saleRes.data?.data;
        let customer = null;

        // Fetch customer if exists
        if (sale?.customer?.id) {
          const customerRes = await axios.get(
            `${baseURL}/customers/${sale.customer.id}`,
            {
              headers: { Authorization: `Bearer ${token}` },
              signal: controller.signal
            }
          );
          customer = customerRes.data?.data;
        }

        if (isMounted) {
          updateStateWithData(sale, customer);
        }
      } catch (err) {
        if (isMounted && err.name !== 'AbortError') {
          console.log("Online fetch failed, trying offline...", err);
          
          // Check if this is an offline sale
          if (sale_id.startsWith('offline_')) {
            const offlineSale = loadOfflineSale(sale_id);
            if (offlineSale) {
              const customer = offlineSale.customer_id 
                ? { id: offlineSale.customer_id, ...offlineSale.customer }
                : null;
              
              updateStateWithData(offlineSale, customer, true);
              return;
            }
          }
          
          // Fallback to showing error
          if (isMounted) {
            setState(prev => ({ 
              ...prev, 
              isLoading: false,
              isOffline: true 
            }));
          }
        }
      }
    };

    const updateStateWithData = (sale, customer, isOffline = false) => {
      const posSettings = JSON.parse(localStorage.getItem("posSettings") || "{}");
      const loginUser = JSON.parse(localStorage.getItem("login_user") || "{}");

      setState(prev => ({
        ...prev,
        sale,
        customer,
        isOffline,
        businessInfo: {
          heading1: posSettings?.data?.heading_line_1 || "Gateway POS",
          heading2: posSettings?.data?.heading_line_2 || "The Real Save",
          name: posSettings?.data?.business_name || "Gateway POS",
          contact: posSettings?.data?.business_contact || "",
          ntn: posSettings?.data?.ntn_no || "",
          strn: posSettings?.data?.strn_no || "",
          cashier: loginUser?.data?.first_name || "Demo"
        },
        isLoading: false
      }));
    };

    if (sale_id) loadData();

    return () => {
      isMounted = false;
      controller.abort();
    };
  }, [sale_id, baseURL, token]);

  useEffect(() => {
    if (!state.isLoading && !state.isPrinted && state.sale) {
      const printTimer = setTimeout(() => {
        window.print();
        setState(prev => ({ ...prev, isPrinted: true }));
      }, 1000);

      return () => clearTimeout(printTimer);
    }
  }, [state.isLoading, state.isPrinted, state.sale]);

  if (state.isLoading) {
    return (
      <div className="receipt-loading">
        <div className="loading-spinner"></div>
        <p>Preparing your receipt...</p>
      </div>
    );
  }

  if (!state.sale) {
    return (
      <div className="receipt-error">
        <p>Unable to generate receipt. {state.isOffline ? "Offline data not found." : "Please check your connection."}</p>
      </div>
    );
  }

  const { sale, customer, businessInfo, isOffline } = state;

  return (
    <div id="receipt-container">
      {isOffline && (
        <div className="offline-notice">
          <p>OFFLINE RECEIPT - NOT SYNCED TO SERVER</p>
        </div>
      )}
      
      <div className="receipt-paper">
        {/* Store Header */}
        <header className="store-header">
          <div className="store-brand">
            <h1 className="store-name">{businessInfo.heading1}</h1>
            <p className="store-slogan">{businessInfo.heading2}</p>
          </div>
          <div className="store-contact">
            <p>{businessInfo.name}</p>
            <p>{businessInfo.contact}</p>
          </div>
        </header>

        {/* Receipt Details */}
        <div className="receipt-details">
          <div className="detail-row">
            <span>Receipt #:</span>
            <strong>{sale.reference || "N/A"}</strong>
          </div>
          <div className="detail-row">
            <span>Date:</span>
            <strong>{sale.date || "N/A"}</strong>
          </div>
          <div className="detail-row">
            <span>Cashier:</span>
            <strong>{businessInfo.cashier}</strong>
          </div>
          {isOffline && (
            <div className="detail-row offline">
              <span>Status:</span>
              <strong>OFFLINE SALE</strong>
            </div>
          )}
        </div>

         {/* Customer Info */}
         {customer && (
          <div className="customer-section">
            <h3 className="section-title">CUSTOMER</h3>
            <div className="customer-details">
              <div className="detail-row">
                <span>Name:</span>
                <span>{customer.name}</span>
              </div>
              <div className="detail-row">
                <span>Phone:</span>
                <span>{customer.phone}</span>
              </div>
              {customer.address && (
                <div className="detail-row">
                  <span>Address:</span>
                  <span>{customer.address}</span>
                </div>
              )}
            </div>
          </div>
        )}

        {/* Items Table */}
        <div className="items-section">
          <h3 className="section-title">ITEMS</h3>
          <div className="items-header">
            <div className="item-col name">Description</div>
            <div className="item-col qty">Qty</div>
            <div className="item-col price">Price</div>
            <div className="item-col total">Total</div>
          </div>
          <div className="items-list">
            {sale.items?.length > 0 ? (
              sale.items.map((item, index) => (
                <div className="item-row" key={`item-${index}`}>
                  <div className="item-col name">{item.product_name || "Product"}</div>
                  <div className="item-col qty">{item.quantity || 0}</div>
                  <div className="item-col price">£{Number(item.price || 0).toFixed(2)}</div>
                  <div className="item-col total">£{Number(item.grand_total || 0).toFixed(2)}</div>
                </div>
              ))
            ) : (
              <div className="no-items">No items in this transaction</div>
            )}
          </div>
        </div>

        {/* Payment Summary */}
        <div className="payment-summary">
          <h3 className="section-title">PAYMENT SUMMARY</h3>
          <div className="summary-row">
            <span>Subtotal:</span>
            <span>£{Number(sale.total_amount || 0).toFixed(2)}</span>
          </div>
          <div className="summary-row">
            <span>Amount Paid:</span>
            <span>£{(Number(sale.total_amount || 0) + Number(sale.change_amount || 0)).toFixed(2)}</span>
          </div>
          <div className="summary-row">
            <span>Change:</span>
            <span>£{Number(sale.change_amount || 0).toFixed(2)}</span>
          </div>
          <div className="summary-total">
            <span>TOTAL:</span>
            <span>£{Number(sale.total_amount || 0).toFixed(2)}</span>
          </div>
        </div>

        {/* Footer */}
        <footer className="receipt-footer">
          <div className="thank-you">
            <p>Thank you for shopping with us!</p>
            <p>We appreciate your business</p>
          </div>
          <div className="terms">
            <h4>RETURN POLICY</h4>
            <ul>
              <li>Returns accepted within 7 days with receipt</li>
              <li>Original packaging required</li>
              <li>No returns on clearance items</li>
            </ul>
          </div>
          <div className="brand-footer">
            <p>{businessInfo.name}</p>
            <p>Powered by Gateway POS</p>
          </div>
        </footer>
        
      </div>
    </div>
  );
};

export default Receipt;